import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Fab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { CompanyImage, CompanyImageType, CompanyImageUploadData } from '../../../../types/models';
import { CompanyImageService } from '../../../../services/CompanyImageService';
import { CompanyImageGrid, EditImageDialog, ImagePreviewDialog } from './CompanyImageCard';
import { ImageUploader } from './ImageUploader';

export const CompanyImagesManager: React.FC = () => {
  // State
  const [images, setImages] = useState<CompanyImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<CompanyImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<CompanyImageType | 'all'>('all');
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<CompanyImage | null>(null);
  
  // Success message
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load images on component mount
  useEffect(() => {
    loadImages();
  }, []);

  const filterImages = useCallback(() => {
    let filtered = images;

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(image => image.imageType === typeFilter);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(image => 
        image.fileName.toLowerCase().includes(searchLower) || // Updated from imageName
        image.imageType.toLowerCase().includes(searchLower) ||
        (image.uploadedByName && image.uploadedByName.toLowerCase().includes(searchLower)) // Updated from createdByName
      );
    }

    setFilteredImages(filtered);
  }, [images, typeFilter, searchTerm]);

  // Filter images when search term or type filter changes
  useEffect(() => {
    filterImages();
  }, [filterImages]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedImages = await CompanyImageService.getCompanyImages();
      setImages(fetchedImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company images');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (uploadData: CompanyImageUploadData) => {
    try {
      const newImage = await CompanyImageService.uploadCompanyImage(uploadData);
      setImages(prev => [newImage, ...prev]);
      setSuccessMessage('Image uploaded successfully');
      setUploadDialogOpen(false);
    } catch (err) {
      throw err; // Re-throw to let ImageUploader handle the error
    }
  };

  const handleEdit = (image: CompanyImage) => {
    setSelectedImage(image);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (imageId: string, updates: { fileName: string; imageType: CompanyImageType }) => { // Updated from imageName
    try {
      await CompanyImageService.updateCompanyImage(imageId, updates);
      
      // Update local state
      setImages(prev => prev.map(img => 
        img.id === imageId 
          ? { ...img, ...updates }
          : img
      ));
      
      setSuccessMessage('Image updated successfully');
      setEditDialogOpen(false);
      setSelectedImage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update image');
    }
  };

  const handlePreview = (image: CompanyImage) => {
    setSelectedImage(image);
    setPreviewDialogOpen(true);
  };

  const handleDeleteClick = (image: CompanyImage) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedImage) return;

    try {
      await CompanyImageService.deleteCompanyImage(selectedImage.id);
      
      // Remove from local state
      setImages(prev => prev.filter(img => img.id !== selectedImage.id));
      
      setSuccessMessage('Image deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedImage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    }
  };

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value as CompanyImageType | 'all');
  };

  return (
    <Box>
      {/* Upload Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          size="large"
          sx={{
            backgroundColor: 'var(--accent-green)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--accent-green)',
              filter: 'brightness(0.9)'
            }
          }}
        >
          Upload Image
        </Button>
      </Box>

      {/* Filters and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--accent-green)' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'var(--accent-green)'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--accent-green)'
            }
          }}
        />
        
        <FormControl sx={{ 
          minWidth: 180,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: 'var(--accent-green)'
            }
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'var(--accent-green)'
          }
        }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={typeFilter}
            label="Filter by Type"
            onChange={handleTypeFilterChange}
          >
            <MenuItem value="all">All Types</MenuItem>
            {Object.values(CompanyImageType).map((type) => (
              <MenuItem key={type} value={type}>
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {type}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredImages.length} of {images.length} images
        </Typography>
      </Box>

      {/* Images Grid */}
      <CompanyImageGrid
        images={filteredImages}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onPreview={handlePreview}
      />

      {/* Upload Dialog */}
      <ImageUploader
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
      />

      {/* Edit Dialog */}
      <EditImageDialog
        image={selectedImage}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedImage(null);
        }}
        onSave={handleSaveEdit}
      />

      {/* Preview Dialog */}
      <ImagePreviewDialog
        image={selectedImage}
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setSelectedImage(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Company Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedImage?.fileName}"? This action cannot be undone. {/* Updated from imageName */}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Floating Action Button for Mobile */}
      <Fab
        aria-label="upload image"
        onClick={() => setUploadDialogOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', md: 'none' },
          backgroundColor: 'var(--accent-green)',
          color: 'white',
          '&:hover': {
            backgroundColor: 'var(--accent-green)',
            filter: 'brightness(0.9)'
          }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};
